import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { uniqBy, debounce } from 'lodash';
import { useInfiniteScroll } from 'ahooks';
import type { SelectProps } from 'antd/es/select';
type ResponseData<T> = {
  list?: T[];
  total?: number;
};
type ResponseResult<T> = {
  /** 状态码，比如200代表响应成功 */
  code?: number;
  /** 响应数据 */
  data?: ResponseData<T>;
  /** 错误码,用于排查问题 */
  errorCode?: string;
  /** 错误信息，用来说明响应情况 */
  errorMessage?: string;
  /** 用户提示信息，用来引导用户下一步操作或者解释错误原因 */
  msg?: string;
};
export type SearchSelectProps<Params, T, ValueType = any> = SelectProps<ValueType> & {
  request: (params: any) => Promise<ResponseResult<T>>;
  initialOptions?: any[];
  labelKey: string;
  valueKey: string;
  searchKey?: string;
  pageSize?: number;
  params?: Params;
  optionLabelFilter?: (d?: any) => any;
  onScrollToEnd?: () => void;
};

const SearchSelect = ({
  onScrollToEnd,
  initialOptions = [],
  request,
  labelKey,
  searchKey = 'searchKey',
  valueKey,
  pageSize = 10,
  params,
  optionLabelFilter,
  onChange,
  selectList,
  ...props
}: any) => {
  const flag = useRef(false);
  const [isSelcetedVal, setIsSelcetedVal] = useState([]);
  const [searchKeyValue, setSearchKeyValue] = useState('');
  const [options, setOptions] = useState(initialOptions);
  const { loadMore: loadMoreTagOptions, noMore } = useInfiniteScroll(
    async (d?: ResponseData<unknown>) => {
      const r = await request({
        [searchKey]: searchKeyValue,
        page: Math.floor((d?.list?.length || 0) / pageSize) + 1 || 1,
        pageIndex: Math.floor((d?.list?.length || 0) / pageSize) + 1 || 1,
        pageSize,
        ...params,
      });
      return {
        list: r?.data?.data?.list || [],
        total: r?.data?.data?.total || 0,
      };
    },
    {
      reloadDeps: [searchKeyValue],
      isNoMore: d => {
        return d ? d.list.length >= d.total : false;
      },
      onSuccess: res => {
        const result = res.list?.map(item => ({ ...item, chName: item?.chName || item?.ehName })) || [];
        let resultData = optionLabelFilter ? optionLabelFilter(result) : result;
        if (!flag.current) {
          resultData = uniqBy(resultData, labelKey || 'label');
          flag.current = true;
        } else {
          resultData = uniqBy(options.concat(resultData), labelKey || 'label');
        }
        setOptions(resultData);
      },
    }
  );

  const onPopupScroll: React.UIEventHandler = e => {
    e.persist();
    // 判断滑动到底部
    const { scrollTop, scrollHeight, clientHeight } = e.target as Element;
    if (scrollHeight - scrollTop === clientHeight && !noMore) {
      loadMoreTagOptions();
      onScrollToEnd?.();
    }
  };
  const debounceSearch = useMemo(() => {
    const fetcher = (v: string) => {
      setOptions([]);
      setSearchKeyValue(v);
    };
    return debounce(fetcher, 500);
  }, []);
  useEffect(() => {
    setIsSelcetedVal(selectList?.map(item => item?.conBrandId));
  }, [selectList]);
  return (
    <Select
      {...props}
      options={options?.map(item =>
        isSelcetedVal.indexOf(item[valueKey]) === -1 ? { ...item, disabled: false } : { ...item, disabled: true }
      )}
      // onSearch={(v) => {
      //   setOptions([]);
      //   setSearchKeyValue(v);
      // }}
      onSearch={debounceSearch}
      onChange={(value, option) => {
        if (searchKeyValue) {
          setOptions([]);
          setSearchKeyValue('');
        }
        setIsSelcetedVal([value]);
        setOptions(optionLabelFilter(options));
        onChange?.(value, option);
      }}
      fieldNames={{ label: labelKey, value: valueKey }}
      onPopupScroll={onPopupScroll}
    />
  );
};

export default SearchSelect;
